import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import '../styles/PatientOnboarding.css';

const PatientOnboarding = ({ user, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    preferredName: '',
    techLevel: '',
    motivations: [],
    workSituation: '',
    goals: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
          ? [...prev[name], value]
          : prev[name].filter(item => item !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const userRef = doc(db, 'users', user.uid);
      const updateData = {
        onboardingComplete: true,
        preferences: {
          ...formData,
          onboardingCompletedAt: new Date().toISOString()
        }
      };
      
      console.log('Attempting to save onboarding data to Firestore:', {
        userId: user.uid,
        updateData
      });
      
      await updateDoc(userRef, updateData);
      
      console.log('Successfully saved onboarding data to Firestore');
      
      // Verify the data was saved correctly
      const updatedDoc = await getDoc(userRef);
      console.log('Verification - data from Firestore after save:', updatedDoc.data());
      
      onComplete();
    } catch (error) {
      console.error('Error saving onboarding data:', {
        error: error.message,
        errorCode: error.code,
        errorDetails: error
      });
      alert('Failed to save your information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    {
      title: "Welcome to Llama!",
      content: (
        <div className="welcome-screen">
          <h2>Welcome to Llama!</h2>
          <p>We're so glad you're here. Let's take a minute to get to know you better so we can personalize your experience and help you achieve your health goals.</p>
        </div>
      ),
      showBack: false
    },
    {
      title: "Preferred Name",
      content: (
        <div className="form-group">
          <label>What would you like us to call you? (This can be different from your legal name)</label>
          <input
            type="text"
            name="preferredName"
            value={formData.preferredName}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Enter your preferred name"
          />
        </div>
      ),
      showBack: true
    },
    {
      title: "Tech Comfort Level",
      content: (
        <div className="form-group">
          <label>How comfortable are you with technology?</label>
          {['Very comfortable', 'Somewhat comfortable', 'Not very comfortable', 'Need extra help'].map(option => (
            <div key={option} className="radio-option">
              <input
                type="radio"
                id={option}
                name="techLevel"
                value={option}
                checked={formData.techLevel === option}
                onChange={handleInputChange}
              />
              <label htmlFor={option}>{option}</label>
            </div>
          ))}
        </div>
      ),
      showBack: true
    },
    {
      title: "Motivations",
      content: (
        <div className="form-group">
          <label>What motivates you most to take care of your health? (Select all that apply)</label>
          {['Family and loved ones', 'Work or career goals', 'Personal independence', 'Avoiding pain', 'Staying active', 'Financial security', 'Quality of life', 'Future health'].map(option => (
            <div key={option} className="checkbox-option">
              <input
                type="checkbox"
                id={`motivation-${option}`}
                name="motivations"
                value={option}
                checked={formData.motivations.includes(option)}
                onChange={handleInputChange}
              />
              <label htmlFor={`motivation-${option}`}>{option}</label>
            </div>
          ))}
        </div>
      ),
      showBack: true
    },
    {
      title: "Work Situation",
      content: (
        <div className="form-group">
          <label>How would you describe your current work situation?</label>
          {['Working full-time', 'Working part-time', 'Self-employed', 'Not currently working', 'Retired', 'Student', 'Carer'].map(option => (
            <div key={option} className="radio-option">
              <input
                type="radio"
                id={option}
                name="workSituation"
                value={option}
                checked={formData.workSituation === option}
                onChange={handleInputChange}
              />
              <label htmlFor={option}>{option}</label>
            </div>
          ))}
        </div>
      ),
      showBack: true
    },
    {
      title: "Your Goals",
      content: (
        <div className="form-group">
          <label>What's your main goal for the next 3 months? (Select all that apply)</label>
          {[
            'Reduce pain levels', 
            'Improve mobility', 
            'Sleep better', 
            'Return to work', 
            'Stay active', 
            'Learn more about my condition', 
            'Manage stress/mood', 
            'Prevent condition from worsening'
          ].map(option => (
            <div key={option} className="checkbox-option">
              <input
                type="checkbox"
                id={`goal-${option}`}
                name="goals"
                value={option}
                checked={formData.goals.includes(option)}
                onChange={handleInputChange}
              />
              <label htmlFor={`goal-${option}`}>{option}</label>
            </div>
          ))}
        </div>
      ),
      showBack: true
    },
    {
      title: "All Set!",
      content: (
        <div className="completion-screen">
          <h2>Thanks for sharing this information with us!</h2>
          <p>We'll use these details to personalize your experience and suggest the most helpful resources for you. You can always update these preferences in your profile settings.</p>
          <p>Let's start your journey to better health management together!</p>
        </div>
      ),
      showBack: false
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const canProceed = () => {
    switch(currentStep) {
      case 1: return formData.preferredName.trim() !== '';
      case 2: return formData.techLevel !== '';
      case 3: return formData.motivations.length > 0;
      case 4: return formData.workSituation !== '';
      case 5: return formData.goals.length > 0;
      default: return true;
    }
  };

  return (
    <div className="onboarding-container">
      {currentStep > 0 && currentStep < steps.length - 1 && (
          <div className="progress-bar">
            <div 
              className="progress" 
              style={{ width: `${(currentStep / (steps.length - 2)) * 100}%` }}
            ></div>
          </div>
        )}
      <div className="onboarding-card">
        
        
        <div className="step-content">
          {currentStep > 0 && currentStep < steps.length - 1 && (
            <h2 className="step-title">{currentStepData.title}</h2>
          )}
          {currentStepData.content}
        </div>
        
        <div className="step-actions">
          {currentStepData.showBack && (
            <button 
              onClick={handleBack}
              className="btn btn-secondary"
              disabled={currentStep === 0}
            >
              Back
            </button>
          )}
          
          {!isLastStep ? (
            <button
              onClick={handleNext}
              className="btn btn-primary"
              disabled={!canProceed()}
            >
              {currentStep === 0 ? "Let's go" : 'Next'}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Continue to Dashboard'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientOnboarding;
